/**
 * @fileoverview Simplified authentication hook
 * @description Provides basic auth status checking and form validation
 *
 * This hook provides simple authentication status checking and form validation.
 * For auth operations (login, signup, etc.), use RTK Query hooks directly
 * from auth.service.ts for better error handling and state management.
 *
 * @see {@link src/services/auth.service.ts} - Auth service with RTK Query hooks
 * @see {@link src/types/userAuth.ts} - Auth types and helper functions
 * @see {@link src/utils/authHelpers.ts} - Reusable auth utility functions
 */

import { useCallback, useEffect, useState } from "react";
import { createClientComponent } from "@/app/utils/supabase/client";
import { Session } from "@supabase/supabase-js";
import { authValidation } from "@/utils/authHelpers";

/**
 * Options for form validation
 * @see {@link src/types/userAuth.ts} - Form data interfaces that use this validation
 */
interface FormValidationOptions {
  /**
   * Required fields to check in form data
   * @example
   * // For SignInFormData
   * ['email', 'password']
   *
   * // For SignUpFormData
   * ['userFirstName', 'userLastName', 'email', 'password', 'confirmPassword']
   */
  requiredFields: string[];
}

/**
 * Hook return type with simplified interface
 */
interface UseAuthReturn {
  /** Whether the user is logged in */
  isLoggedIn: boolean;
  /** Whether auth status is being checked */
  isCheckingAuth: boolean;
  /** The current user session if available */
  session: Session | null;
  /**
   * Verify form data is complete
   * @param data - Form data object to validate
   * @returns True if all required fields are present and non-empty
   */
  verifyFormData: (data: Record<string, any>) => boolean;
}

/**
 * Simplified auth hook for checking auth status and form validation
 *
 * For auth operations like login/signup, use the RTK Query hooks directly:
 * - useUserSignInMutation - For email/password login
 * - useUserSignUpMutation - For registration
 * - useGoogleAuthMutation - For Google OAuth
 * - useLinkedInAuthMutation - For LinkedIn OAuth
 * - etc.
 *
 * This hook is used across multiple auth components to provide consistent validation:
 * @see {@link src/components/auth/SignIn.tsx} - Uses for email/password validation
 * @see {@link src/components/auth/SignUp.tsx} - Uses for registration form validation
 * @see {@link src/components/auth/MagicSignIn/MagicSignIn.tsx} - Uses for email validation
 * @see {@link src/components/auth/forgotPassword/index.tsx} - Uses for email validation
 *
 * @example
 * // Check if user is logged in
 * const { isLoggedIn, isCheckingAuth } = useAuth();
 *
 * // Validate form fields
 * const { verifyFormData } = useAuth({ requiredFields: ['email', 'password'] });
 * const isValid = verifyFormData(formData);
 */
export const useAuth = (options?: FormValidationOptions): UseAuthReturn => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const supabase = createClientComponent();
        const { data } = await supabase.auth.getSession();
        setIsLoggedIn(!!data.session);
        setSession(data.session);
      } catch (error) {
        console.error("Error checking auth status:", error);
        setIsLoggedIn(false);
        setSession(null);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthStatus();

    // Set up auth state change listener
    const supabase = createClientComponent();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setIsLoggedIn(!!newSession);
      setSession(newSession);
    });

    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Verify all required form fields are filled
   * This is a simple validation to ensure all required fields have values
   * For more complex validation, see validations/form_validations.ts
   *
   * @param {Record<string, any>} data - Form data object to validate
   * @returns {boolean} True if all required fields are filled
   */
  const verifyFormData = useCallback(
    (data: Record<string, any>): boolean => {
      return authValidation.verifyFormData(data, options?.requiredFields);
    },
    [options?.requiredFields]
  );

  return {
    isLoggedIn,
    isCheckingAuth,
    session,
    verifyFormData,
  };
};

export default useAuth;
